import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingCart, Tag, Search, CalendarClock, Globe2, Wrench, Car, ShieldCheck,
  ArrowRight, Apple, Play, CheckCircle2, Sparkles, Star, Quote,
} from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/Sections";
import { VehicleCard } from "@/components/VehicleCard";
import { Button } from "@/components/ui/button";

const HERO = "https://images.pexels.com/photos/14487132/pexels-photo-14487132.jpeg?auto=compress&cs=tinysrgb&w=1600";
const IMPORT_IMG = "https://images.unsplash.com/photo-1601929862217-f1bf94503333?w=1400";
const WORKSHOP_IMG = "https://images.unsplash.com/photo-1570129476815-ba368ac77013?w=1400";

const heroButtons = [
  { to: "/vehicules", label: "Acheter un véhicule", icon: ShoppingCart },
  { to: "/vendre", label: "Vendre mon véhicule", icon: Tag },
  { to: "/recherche", label: "Recherche personnalisée", icon: Search },
  { to: "/atelier", label: "Prendre rendez-vous", icon: CalendarClock },
];

const services = [
  { icon: ShoppingCart, title: "Achat & Vente", desc: "Véhicules neufs et d'occasion, reprise et estimation de votre ancien véhicule.", to: "/vehicules" },
  { icon: Search, title: "Recherche personnalisée", desc: "Indiquez vos critères, nous trouvons le véhicule qu'il vous faut.", to: "/recherche" },
  { icon: Globe2, title: "Importation", desc: "Un véhicule à l'étranger ? Nous gérons tout, de la recherche à l'administratif.", to: "/importation" },
  { icon: Wrench, title: "Réparation & Entretien", desc: "Atelier toutes marques : entretien, diagnostic et réparations mécaniques.", to: "/atelier" },
];

const whyUs = [
  { icon: Car, title: "Une solution complète", desc: "Achat, vente, importation, recherche, entretien et réparation : un seul interlocuteur." },
  { icon: Wrench, title: "Toutes marques", desc: "Notre atelier prend en charge les véhicules de toutes les marques." },
  { icon: Sparkles, title: "Accompagnement personnalisé", desc: "Chaque projet est différent. Nous prenons le temps de comprendre vos besoins." },
  { icon: ShieldCheck, title: "Simple & transparent", desc: "Clarté, sérieux et professionnalisme à chaque étape de votre projet." },
];

const reviews = [
  { name: "Julien M.", initial: "J", text: "Accompagnement au top pour l'achat de mon véhicule. Équipe à l'écoute, transparente et de bon conseil. Je recommande vivement AS AUTO !" },
  { name: "Sophie L.", initial: "S", text: "J'ai fait importer une voiture introuvable en France. Tout a été géré de A à Z, démarches administratives comprises. Un vrai gain de temps." },
  { name: "Karim B.", initial: "K", text: "Entretien et réparation impeccables sur ma berline. Diagnostic précis, tarifs clairs et délais respectés. Garage sérieux." },
];

const REVIEWS_URL = "https://www.google.com/maps/search/?api=1&query=AS+AUTO+3+rue+de+Kerandr%C3%A9+56700+Hennebont";

export default function Home() {
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/vehicles", { params: { featured: true } }).then((r) => setVehicles(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO} alt="AS AUTO" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
          <div className="absolute inset-0 hero-scrim" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full pb-16 pt-28">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6"
          >
            Achat • Vente • Importation • Recherche • Réparation
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white max-w-4xl leading-[0.98]"
          >
            Votre partenaire automobile, de l'achat à la réparation.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-6 text-lg text-neutral-300 max-w-2xl leading-relaxed"
          >
            Bienvenue chez AS AUTO. Que vous cherchiez votre prochaine voiture, souhaitiez vendre,
            importer ou réparer votre véhicule, nous vous accompagnons à chaque étape.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
          >
            {heroButtons.map((b, i) => (
              <Link
                key={b.to}
                to={b.to}
                data-testid={`hero-btn-${b.to.slice(1) || "home"}`}
                className="group relative flex items-center gap-3 bg-white/5 hover:bg-[#C5A059] border border-white/15 hover:border-[#C5A059] backdrop-blur-md rounded-sm px-5 py-4 transition-all duration-300 active:scale-95"
              >
                <b.icon className="h-5 w-5 text-[#C5A059] group-hover:text-black transition-colors" />
                <span className="text-sm font-semibold text-white group-hover:text-black transition-colors">{b.label}</span>
                <ArrowRight className="h-4 w-4 ml-auto text-white/40 group-hover:text-black group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <SectionHeading
          overline="Un garage pour tous vos besoins"
          title="Un interlocuteur unique pour votre automobile"
          subtitle="De l'achat de votre véhicule jusqu'à son entretien, en passant par la recherche personnalisée et l'importation, notre équipe vous accompagne."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <Link
                to={s.to}
                data-testid={`service-card-${i}`}
                className="group flex h-full flex-col bg-[#121212] border border-white/10 rounded-sm p-7 transition-all duration-300 hover:-translate-y-1 hover:border-white/25"
              >
                <s.icon className="h-8 w-8 text-[#C5A059]" />
                <h3 className="font-display text-xl font-semibold text-white mt-6">{s.title}</h3>
                <p className="text-sm text-neutral-400 mt-3 leading-relaxed flex-1">{s.desc}</p>
                <span className="mt-6 flex items-center gap-2 text-sm text-[#C5A059] font-medium">
                  En savoir plus <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED VEHICLES */}
      {vehicles.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <SectionHeading overline="Nos véhicules" title="Notre sélection du moment" />
            <Link to="/vehicules">
              <Button data-testid="see-all-vehicles" variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-sm">
                Voir tous les véhicules <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {vehicles.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.08}><VehicleCard vehicle={v} /></Reveal>
            ))}
          </div>
        </section>
      )}

      {/* IMPORT + WORKSHOP BENTO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div className="relative h-full min-h-[380px] rounded-sm overflow-hidden border border-white/10 group">
            <img src={IMPORT_IMG} alt="Importation" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <div className="relative z-10 flex h-full flex-col justify-end p-8">
              <Globe2 className="h-8 w-8 text-[#C5A059] mb-4" />
              <h3 className="font-display text-3xl font-bold text-white">Importation de véhicules</h3>
              <p className="text-neutral-300 mt-3 max-w-md">Recherche → Sélection → Vérification → Achat → Importation → Accompagnement administratif.</p>
              <Link to="/importation" className="mt-6">
                <Button className="bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm">Parlez-nous de votre projet</Button>
              </Link>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative h-full min-h-[380px] rounded-sm overflow-hidden border border-white/10 group">
            <img src={WORKSHOP_IMG} alt="Atelier" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <div className="relative z-10 flex h-full flex-col justify-end p-8">
              <Wrench className="h-8 w-8 text-[#C5A059] mb-4" />
              <h3 className="font-display text-3xl font-bold text-white">Atelier toutes marques</h3>
              <p className="text-neutral-300 mt-3 max-w-md">Entretien, diagnostic électronique, freinage, distribution, climatisation, mécanique générale.</p>
              <Link to="/atelier" className="mt-6">
                <Button className="bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm">Prendre rendez-vous</Button>
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <SectionHeading overline="Pourquoi choisir AS AUTO ?" title="Écoute • Transparence • Réactivité • Professionnalisme" align="center" />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {whyUs.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08}>
              <div data-testid={`why-card-${i}`} className="h-full bg-[#121212] border border-white/10 rounded-sm p-7">
                <w.icon className="h-7 w-7 text-[#C5A059]" />
                <h3 className="font-display text-lg font-semibold text-white mt-5">{w.title}</h3>
                <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AVIS CLIENTS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <SectionHeading overline="Avis clients" title="Ils nous ont fait confiance" />
          <a href={REVIEWS_URL} target="_blank" rel="noreferrer">
            <Button data-testid="google-reviews-btn" variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-sm">
              Voir nos avis Google <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </a>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {reviews.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.08}>
              <div data-testid={`review-card-${i}`} className="h-full bg-[#121212] border border-white/10 rounded-sm p-7 flex flex-col">
                <Quote className="h-7 w-7 text-[#C5A059]/40" />
                <div className="flex gap-0.5 mt-4">
                  {[0, 1, 2, 3, 4].map((s) => <Star key={s} className="h-4 w-4 fill-[#C5A059] text-[#C5A059]" />)}
                </div>
                <p className="text-neutral-300 mt-4 leading-relaxed flex-1">{r.text}</p>
                <div className="flex items-center gap-3 mt-6 pt-5 border-t border-white/10">
                  <div className="h-10 w-10 rounded-full bg-[#C5A059]/15 flex items-center justify-center font-display font-bold text-[#C5A059]">{r.initial}</div>
                  <div>
                    <p className="text-sm font-semibold text-white">{r.name}</p>
                    <p className="text-xs text-neutral-500">Avis Google</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* APP CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-sm border border-white/10 bg-[#0f0f0f] p-10 lg:p-16">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#C5A059]/10 blur-3xl" />
            <div className="relative z-10 max-w-2xl">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059] mb-4">L'application AS AUTO</p>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-white leading-[1.05]">AS AUTO dans votre poche.</h2>
              <p className="mt-5 text-neutral-400 leading-relaxed">
                Consultez nos véhicules, effectuez une recherche personnalisée, demandez un devis,
                prenez rendez-vous à l'atelier et suivez vos demandes — le tout au même endroit.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <div className="flex items-center gap-3 bg-black border border-white/15 rounded-sm px-5 py-3">
                  <Apple className="h-6 w-6 text-white" />
                  <div className="text-left leading-tight"><span className="block text-[10px] text-neutral-400">Télécharger sur l'</span><span className="block text-sm font-semibold text-white">App Store</span></div>
                </div>
                <div className="flex items-center gap-3 bg-black border border-white/15 rounded-sm px-5 py-3">
                  <Play className="h-6 w-6 text-white" />
                  <div className="text-left leading-tight"><span className="block text-[10px] text-neutral-400">Disponible sur</span><span className="block text-sm font-semibold text-white">Google Play</span></div>
                </div>
              </div>
              <Link to="/application" className="mt-6 inline-flex items-center gap-2 text-sm text-[#C5A059] font-medium">
                Découvrir l'application <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
